import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    LayoutDashboard,
    Newspaper,
    TrendingUp,
    MessageSquare,
    Settings,
    LogOut,
    Users,
    Bell,
    Menu,
    X,
    ChevronRight,
    Sparkles,
    Activity,
    Building2,
    Briefcase
} from 'lucide-react';
import api from '../../services/api';

const AdminLayout = ({ children, title }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [stats, setStats] = useState({ news: 0, ads: 0, messages: 0 });

    // Real-time stats update
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.admin.getDashboardStats();
                if (response.ok) {
                    const data = await response.json();
                    setUnreadCount(data.unread_contacts || 0);
                    setStats({
                        news: data.total_news || 0,
                        ads: data.active_ads || 0,
                        messages: data.unread_contacts || 0
                    });
                }
            } catch (e) {
                console.error(e);
            }

            // Also get local counts
            try {
                const localNews = JSON.parse(localStorage.getItem('siteNews') || '[]');
                const localAds = JSON.parse(localStorage.getItem('siteAds') || '[]');
                setStats(prev => ({
                    ...prev,
                    news: localNews.length || prev.news,
                    ads: localAds.filter(a => a.active).length || prev.ads
                }));
            } catch (e) { }
        };

        if (user) {
            fetchStats();
            // Poll for real-time updates
            const interval = setInterval(fetchStats, 5000);
            return () => clearInterval(interval);
        }
    }, [user]);

    const handleLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    const navItems = [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', color: 'from-blue-500 to-indigo-500' },
        { path: '/admin/news', icon: Newspaper, label: 'News & Events', badge: stats.news, color: 'from-emerald-500 to-teal-500' },
        { path: '/admin/ads', icon: TrendingUp, label: 'Ad Management', badge: stats.ads, color: 'from-purple-500 to-pink-500' },
        { path: '/admin/branches', icon: Building2, label: 'Branches', color: 'from-teal-500 to-cyan-500' },
        { path: '/admin/careers', icon: Briefcase, label: 'Careers', color: 'from-blue-600 to-indigo-600' },
        { path: '/admin/contacts', icon: MessageSquare, label: 'Messages', badge: unreadCount, isAlert: unreadCount > 0, color: 'from-amber-500 to-orange-500' },
        { path: '/admin/users', icon: Users, label: 'Users', color: 'from-cyan-500 to-blue-500' },
        { path: '/admin/settings', icon: Settings, label: 'Settings', color: 'from-gray-500 to-slate-500' },
    ];

    const SidebarContent = () => (
        <>
            {/* Logo */}
            <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-white">Admin</h1>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Pride Path</p>
                        </div>
                    </div>
                    <button className="md:hidden text-white/70 hover:text-white" onClick={() => setIsMobileMenuOpen(false)}>
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Live Indicator */}
            <div className="px-4 py-3 mx-4 mt-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center gap-2">
                    <div className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </div>
                    <span className="text-xs text-gray-400">Live Updates Active</span>
                    <Activity className="w-3 h-3 text-emerald-400 ml-auto" />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                                ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg'
                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            <item.icon className={`w-5 h-5 transition-transform ${isActive ? '' : 'group-hover:scale-110'}`} />
                            <span className="font-medium">{item.label}</span>
                            {item.badge > 0 && (
                                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${item.isAlert
                                    ? 'bg-red-500 text-white animate-pulse'
                                    : isActive
                                        ? 'bg-white/20 text-white'
                                        : 'bg-white/10 text-gray-300'
                                    }`}>
                                    {item.badge}
                                </span>
                            )}
                            {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                        </Link>
                    );
                })}
            </nav>

            {/* User Profile & Logout */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 px-3 py-2 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                        {user?.email?.[0]?.toUpperCase() || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.email || 'Admin'}</p>
                        <p className="text-xs text-gray-500">Administrator</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 w-full text-left bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-all"
                >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Sign Out</span>
                </button>
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-slate-900 flex">
            {/* Animated Background */}
            <div className="fixed inset-0 z-0">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
            </div>

            {/* Desktop Sidebar */}
            <aside className="hidden md:flex flex-col w-72 bg-slate-800/50 backdrop-blur-xl fixed h-full shadow-2xl z-20 border-r border-white/5">
                <SidebarContent />
            </aside>

            {/* Mobile Sidebar */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
                    <aside className="absolute left-0 top-0 bottom-0 w-72 bg-slate-800/95 backdrop-blur-xl flex flex-col shadow-2xl border-r border-white/5">
                        <SidebarContent />
                    </aside>
                </div>
            )}

            {/* Main Content */}
            <main className="flex-1 md:ml-72 p-4 md:p-8 overflow-x-hidden w-full relative z-10">
                {/* Top Header */}
                <header className="flex justify-between items-center mb-8 bg-white/5 backdrop-blur-xl p-4 md:p-5 rounded-2xl shadow-lg border border-white/10">
                    <div className="flex items-center gap-4">
                        <button className="md:hidden text-gray-400 hover:text-white" onClick={() => setIsMobileMenuOpen(true)}>
                            <Menu className="w-6 h-6" />
                        </button>
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-white">{title || 'Dashboard'}</h2>
                            <p className="text-xs text-gray-500 hidden md:block">Manage your website content</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Notification Bell */}
                        <button className="relative p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/10">
                            <Bell className="w-5 h-5 text-gray-400" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                                    {unreadCount}
                                </span>
                            )}
                        </button>

                        {/* User Avatar */}
                        <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-white/10">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-teal-500/25">
                                {user?.email?.[0]?.toUpperCase() || 'A'}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <div className="relative">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
